import { Link } from "@inertiajs/react"
import { ClipboardList, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader, SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  PURCHASING_PATHS,
  purchaseTone,
  tPurchasing,
  type Purchase,
} from "@/features/staff/purchasing/purchasing"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

export type PurchasesWorkspaceProps = {
  drafts: Purchase[]
  history: Purchase[]
  historyTotal: number
  historyLimit: number
  currency: string
  timeZone: string | null
}

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.purchases.index"

function PurchaseRow({
  purchase,
  currency,
  timeZone,
}: {
  purchase: Purchase
  currency: string
  timeZone: string | null
}) {
  const stamp = purchase.receivedAt ?? purchase.cancelledAt ?? purchase.createdAt

  return (
    <Link
      href={PURCHASING_PATHS.purchase(purchase.id)}
      className="hover:bg-muted/50 focus-visible:ring-ring flex min-h-11 flex-col gap-2 rounded-lg px-4 py-3 transition-colors outline-none focus-visible:ring-[3px] sm:flex-row sm:items-center sm:justify-between md:min-h-9"
      data-testid={`purchase-row-${purchase.id}`}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{purchase.supplierName}</span>
          <StatusBadge
            tone={purchaseTone(purchase.status)}
            label={purchase.statusLabel}
            data-testid={`purchase-status-${purchase.id}`}
          />
        </div>
        <p className="text-muted-foreground text-xs">
          {purchase.invoiceNumber
            ? tPurchasing("row_invoice", { invoice: purchase.invoiceNumber })
            : tPurchasing("row_no_invoice")}
          {" · "}
          {tPurchasing("row_lines", { count: purchase.lineCount })}
          {" · "}
          {formatDateTime(stamp, { timeZone: timeZone ?? undefined })}
        </p>
      </div>

      <p
        className="text-foreground shrink-0 text-sm font-semibold tabular-nums"
        data-testid={`purchase-total-${purchase.id}`}
      >
        {formatMoney(purchase.totalCost, { currency })}
      </p>
    </Link>
  )
}

/**
 * Deliveries, split by whether they have arrived.
 *
 * ## Drafts are the working list and are never capped
 *
 * A purchase order sitting in draft is a truck somebody is waiting for, so every
 * one of them is on the screen, oldest first: the one that has been open longest
 * is the one to chase. Settled deliveries are history — read to answer a question
 * about one invoice, not scanned — so the screen shows the most recent and says
 * how many there are in total rather than pretending the list is complete.
 *
 * ## The row is a link, not a set of buttons
 *
 * Receiving moves stock and repoints every average cost. It happens on the
 * delivery's own screen, in front of the lines it is about, and never from a
 * list where the wrong row is one mis-tap away.
 */
export function PurchasesWorkspace({
  drafts,
  history,
  historyTotal,
  historyLimit,
  currency,
  timeZone,
}: PurchasesWorkspaceProps) {
  const empty = drafts.length === 0 && history.length === 0

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={t(`${PAGE_SCOPE}.title`)}
          description={t(`${PAGE_SCOPE}.description`)}
          actions={
            empty ? undefined : (
              <Button asChild data-testid="purchase-new">
                <Link href={PURCHASING_PATHS.newPurchase}>
                  <Plus className="size-4" />
                  {tPurchasing("purchase_new")}
                </Link>
              </Button>
            )
          }
        />

        {empty ? (
          <Card data-testid="purchases-empty">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <ClipboardList className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tPurchasing("purchases_empty_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">
                {tPurchasing("purchases_empty_hint")}
              </p>
              <Button asChild data-testid="purchase-new-empty">
                <Link href={PURCHASING_PATHS.newPurchase}>
                  <Plus className="size-4" />
                  {tPurchasing("purchase_new")}
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <section data-testid="purchases-drafts">
              <SectionHeader
                title={tPurchasing("section_drafts")}
                trailing={String(drafts.length)}
              />
              <Card density="compact">
                <CardContent className="divide-border divide-y px-0">
                  {drafts.length === 0 ? (
                    <p className="text-muted-foreground px-4 py-3 text-sm">
                      {tPurchasing("drafts_empty")}
                    </p>
                  ) : (
                    drafts.map((purchase) => (
                      <PurchaseRow
                        key={purchase.id}
                        purchase={purchase}
                        currency={currency}
                        timeZone={timeZone}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            </section>

            <section data-testid="purchases-history">
              <SectionHeader
                title={tPurchasing("section_history")}
                trailing={String(historyTotal)}
              />
              <Card density="compact">
                <CardContent className="divide-border divide-y px-0">
                  {history.length === 0 ? (
                    <p className="text-muted-foreground px-4 py-3 text-sm">
                      {tPurchasing("history_empty")}
                    </p>
                  ) : (
                    history.map((purchase) => (
                      <PurchaseRow
                        key={purchase.id}
                        purchase={purchase}
                        currency={currency}
                        timeZone={timeZone}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
              {historyTotal > historyLimit && (
                <p className="text-muted-foreground mt-2 px-4 text-xs" data-testid="history-capped">
                  {tPurchasing("history_capped", { shown: history.length, total: historyTotal })}
                </p>
              )}
            </section>
          </div>
        )}
      </div>
    </MainContainer>
  )
}
